import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { Footer, Nav } from "./chrome";
import { ClubsView } from "./clubs-view";
import type { Club, ThemeRegion } from "../../lib/api/lidoo.functions";
import { normalizeFacilities } from "../../lib/facilities";
import { fill, regionName, useI18n } from "../../lib/i18n";
import { MIN_CLUBS_PER_THEME_PAGE, THEMES, themeSlug, type Theme } from "../../lib/themes";

// The block of theme links that sits at the bottom of a region page.
//
// Counted here rather than fetched, because the region page already holds every
// club on the coast and the answer is a pass over an array it has in hand.
// Links below the threshold are left out, so this never offers a page that
// would 404: the same constant guards both ends.
export function ThemeLinks({ clubs, regionKey }: { clubs: Club[]; regionKey: string }) {
  const { t } = useI18n();

  const available = useMemo(() => {
    const counts = new Map<Theme, number>();
    for (const c of clubs) {
      const own = new Set(normalizeFacilities(c.facilities, c.amenities));
      for (const th of THEMES) {
        if (own.has(th)) counts.set(th, (counts.get(th) ?? 0) + 1);
      }
    }
    return THEMES.map((th) => [th, counts.get(th) ?? 0] as const).filter(
      ([, n]) => n >= MIN_CLUBS_PER_THEME_PAGE,
    );
  }, [clubs]);

  if (available.length === 0) return null;

  return (
    <section className="border-t border-[#1C2430]/15 pt-8">
      <h2 className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
        {t("th_browse")}
      </h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {available.map(([th, n]) => (
          <Link
            key={th}
            to="/theme/$theme/$region"
            params={{ theme: themeSlug(th), region: regionKey }}
            className="li-mono rounded-full border border-[#1C2430]/20 px-4 py-2 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
          >
            {t(`th_${th}`)} <span className="text-[#5B6472]">({n})</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// The body of every theme page, rendered by all twelve routes: eight themes
// crossed with a region or not, in six languages. Kept as one component with
// props for the same reason the club page is: the /nl route must render this
// from its own loader rather than fall through to the English one.
export function ThemePage({
  theme,
  clubs,
  regions,
  regionKey,
  regionLabel,
}: {
  theme: Theme;
  clubs: Club[];
  regions: ThemeRegion[];
  regionKey?: string;
  regionLabel?: string;
}) {
  const { t } = useI18n();
  const what = t(`th_${theme}`);
  const place = regionKey ? regionName(regionKey, regionLabel ?? "", t) : null;
  const heading = place ? fill(t("th_in"), { what, place }) : what;

  // On a regional page, the same theme on every other coast. On the Europe-wide
  // page, every coast. Same data either way, so the block is built once.
  const others = regions.filter((r) => r.regionKey !== regionKey);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: heading,
    itemListElement: clubs.slice(0, 50).map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://sunbedly.com/club/${c.slug}`,
      name: c.name,
    })),
  };

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-20">
        <nav
          aria-label="Breadcrumb"
          className="li-mono flex flex-wrap items-center gap-1.5 pt-8 text-[12px] text-[#5B6472]"
        >
          <Link to="/" className="hover:text-[#2C4FD6]">
            {t("crumb_home")}
          </Link>
          <span aria-hidden="true">/</span>
          <Link to="/clubs" className="hover:text-[#2C4FD6]">
            {t("nav_explore")}
          </Link>
          {place ? (
            <>
              <span aria-hidden="true">/</span>
              <Link
                to="/theme/$theme"
                params={{ theme: themeSlug(theme) }}
                className="hover:text-[#2C4FD6]"
              >
                {what}
              </Link>
              <span aria-hidden="true">/</span>
              <span className="text-[#1C2430]">{place}</span>
            </>
          ) : (
            <>
              <span aria-hidden="true">/</span>
              <span className="text-[#1C2430]">{what}</span>
            </>
          )}
        </nav>

        <header className="border-b border-[#1C2430]/15 pb-6 pt-6">
          <h1 className="li-serif text-3xl font-normal text-[#1C2430] md:text-5xl">{heading}</h1>
          <p className="li-mono mt-2 text-[12px] text-[#5B6472]">
            {fill(t("th_count"), { n: String(clubs.length) })}
          </p>
          <p className="mt-4 max-w-[68ch] text-sm leading-relaxed text-[#5B6472]">
            {t("th_intro")}
          </p>
        </header>

        <div className="mt-8">
          <ClubsView clubs={clubs} />
        </div>

        {others.length > 0 ? (
          <section className="mt-16 border-t border-[#1C2430]/15 pt-8">
            <h2 className="li-mono text-[11px] uppercase tracking-widest text-[#5B6472]">
              {place ? t("th_other_regions") : t("th_all_regions")}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {others.map((r) => (
                <Link
                  key={r.regionKey}
                  to="/theme/$theme/$region"
                  params={{ theme: themeSlug(theme), region: r.regionKey }}
                  className="li-mono rounded-full border border-[#1C2430]/20 px-4 py-2 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
                >
                  {regionName(r.regionKey, r.regionLabel, t)}{" "}
                  <span className="text-[#5B6472]">({r.count})</span>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {/* Back to the whole coast, not just this slice of it: someone who came
            in on "dog-friendly Sicily" is one click from all of Sicily. */}
        {place && regionKey ? (
          <div className="mt-8">
            <Link
              to="/region/$key"
              params={{ key: regionKey }}
              className="li-mono text-[12px] text-[#2C4FD6] underline underline-offset-4"
            >
              {fill(t("region_meta_title"), { place })} →
            </Link>
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
